/**
 * The execution server behind one installed capability plugin.
 *
 * Reads the servers list and picks this plugin's row. The supervisor's view
 * (state, restarts, last error) is always present; the server's own status
 * (running, queued, instances) is present only while it answers: ready or
 * draining. Polled while the card is mounted, since a server comes and goes
 * on its own.
 *
 * An operator's restart (optum-agent Feature 87, D15) reads on the badge as
 * Draining with the count still running, then Starting, then Running; the
 * card names who restarted it last and when.
 */
import { useQuery } from '@tanstack/react-query';

import { listPluginServers } from '@optum/api-client/endpoints/plugins';
import type { PluginServer, PluginServerState } from '@optum/api-client/types';
import type { ReactNode } from 'react';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { relativeTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { Badge, Typography, type BadgeTone } from '../../ui/finixt.js';

const POLL_MS = 5000;
/**
 * Narrowest a fact column may get, in pixels. The longest value is a relative
 * time such as "59 minutes ago", about 110px in mono at body size; the grid
 * fits as many columns as hold this, two in the plugin page's rail.
 */
const FACT_MIN_WIDTH = 120;

/** The badge color for each supervisor state; a failed server is the one to look at. */
export const SERVER_STATE_COLOR: Record<PluginServerState, BadgeTone> = {
  starting: 'info',
  ready: 'ok',
  draining: 'warn',
  failed: 'danger',
  stopped: 'neutral',
};

/** What the card's badge says of each state, in an operator's words. */
const STATE_LABEL: Record<PluginServerState, string> = {
  starting: 'Starting',
  ready: 'Running',
  draining: 'Draining',
  failed: 'Failed',
  stopped: 'Stopped',
};

interface Restarted {
  at: string | null;
  by: string | null;
}

interface Props {
  pluginName: string;
  installedVersion: string | null;
  /** The last operator restart, from the plugin's catalog entry. */
  restarted?: Restarted | undefined;
}

/** Megabytes with no decimals; a server's resident size is read at a glance. */
export function megabytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

export function ExecutionServerCard({ pluginName, installedVersion, restarted }: Props) {
  if (installedVersion === null) return null;
  return <ServerView pluginName={pluginName} restarted={restarted} />;
}

/** "Draining · 2 still running", or the state's word alone. */
function stateLabel(row: PluginServer): string {
  if (row.state === 'draining' && row.server) {
    return `Draining · ${row.server.running} still running`;
  }
  return STATE_LABEL[row.state];
}

function ServerView({
  pluginName,
  restarted,
}: {
  pluginName: string;
  restarted: Restarted | undefined;
}) {
  const query = useQuery({
    queryKey: qk.pluginServers(),
    queryFn: listPluginServers,
    refetchInterval: POLL_MS,
  });

  if (query.isPending) {
    return (
      <SectionCard title="Execution server">
        <SectionCardBody>
          <LoadingBlock />
        </SectionCardBody>
      </SectionCard>
    );
  }
  if (query.isError) {
    return (
      <SectionCard title="Execution server">
        <SectionCardBody>
          <ErrorBlock
            message={query.error instanceof Error ? query.error.message : String(query.error)}
          />
        </SectionCardBody>
      </SectionCard>
    );
  }
  const row = query.data.find((server) => server.dist === pluginName);
  if (!row) {
    return (
      <SectionCard title="Execution server">
        <SectionCardBody>
          <Typography variant="meta" as="p">
            No server for this plugin in the running agent.
          </Typography>
        </SectionCardBody>
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title="Execution server"
      subtitle="One long-lived process per plugin; tasks run on it without a cold start."
      actions={<Badge tone={SERVER_STATE_COLOR[row.state]}>{stateLabel(row)}</Badge>}
    >
      <SectionCardBody>
        <ServerFacts row={row} restarted={restarted} />
      </SectionCardBody>
    </SectionCard>
  );
}

function ServerFacts({ row, restarted }: { row: PluginServer; restarted: Restarted | undefined }) {
  const status = row.server;
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${FACT_MIN_WIDTH}px, 1fr))`,
        columnGap: 16,
        rowGap: 11,
        fontSize: 'var(--text-body-md)',
      }}
    >
      <Fact label="Process id" value={row.pid ?? '—'} mono />
      <Fact label="Resident memory" value={megabytes(status?.rss_bytes)} />
      <Fact
        label="Tasks running"
        value={status ? `${status.running} / ${status.max_concurrency}` : '—'}
      />
      <Fact label="Queued" value={status?.queued ?? '—'} />
      <Fact label="Cached instances" value={status?.instances ?? '—'} />
      <Fact label="Pinned" value={status?.pinned ?? '—'} />
      <Fact label="Abandoned threads" value={status?.abandoned ?? '—'} />
      <Fact label="Restarts" value={row.restarts} />
      <Fact label="Started" value={row.started_at ? relativeTime(row.started_at) : '—'} />
      <Fact
        label="Last served"
        value={status?.last_served_at ? relativeTime(status.last_served_at) : '—'}
      />
      {restarted?.at ? (
        <Fact
          label="Restarted"
          value={`${relativeTime(restarted.at)} by ${restarted.by ?? 'an operator'}`}
          wide
        />
      ) : null}
      <Fact label="Socket" value={row.socket_path} mono wide />
      {row.last_error && <Fact label="Last error" value={row.last_error} wide />}
    </dl>
  );
}

function Fact({
  label,
  value,
  mono = false,
  wide = false,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  wide?: boolean;
}) {
  return (
    <div style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <dt style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>{label}</dt>
      <dd
        className="mono"
        style={mono ? { wordBreak: 'break-all', fontSize: 'var(--text-meta-lg)' } : undefined}
      >
        {value}
      </dd>
    </div>
  );
}
